'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './assets.css';

/* All styling lives in assets.css (scoped to .assets-page).
   Images go in public/assets/images/ .
   "Industry Scenarios" cards: har card ka image path aur link neeche `industries` array me `img` / `href` field me badlo.
   Jis card ka page nahi hai (Auto & Industrial, Facilities Management) uska href nahi hai, wo plain card rahega. */

type IconName = 'predict' | 'spare' | 'health' | 'life' | 'gear' | 'box' | 'coin' | 'clock' | 'shield' | 'q' | 'plus' | 'doc' | 'down';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    predict: <><path d="M4 18 9 12l4 3 7-9" /><path d="M4 4v16h16" /></>,
    spare: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
    health: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 16v-4M12 16V8M16 16v-6" /></>,
    life: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><circle cx="12" cy="12" r="8" /></>,
    box: <><path d="M12 3 4 7.5v9L12 21l8-4.5v-9z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>,
    coin: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3 3 7 3s7-1.3 7-3v-6" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    q: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7M12 17h.01" /></>,
    plus: <><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></>,
    doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5" /></>,
    down: <><path d="M12 4v12M7 11l5 5 5-5M5 20h14" /></>,
  };
  return (
    <svg className="as-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const heroChips: { icon: IconName; label: string }[] = [
  { icon: 'predict', label: 'Predictive Maintenance' },
  { icon: 'spare', label: 'Spare Parts Optimisation' },
  { icon: 'gear', label: 'Maintenance Optimisation' },
  { icon: 'health', label: 'Asset Health Monitoring' },
  { icon: 'life', label: 'Extended Asset Life' },
];

const benefits: { icon: IconName; title: string; text: string }[] = [
  { icon: 'gear', title: 'Higher Asset Availability', text: 'Reduce unplanned downtime and improve OEE.' },
  { icon: 'box', title: 'Optimised Spare Inventory', text: 'Maintain the right parts at the right place and time.' },
  { icon: 'coin', title: 'Lower Maintenance Cost', text: 'Reduce reactive maintenance and emergency procurement.' },
  { icon: 'clock', title: 'Extended Asset Life', text: 'Improve reliability and maximise return on assets.' },
  { icon: 'shield', title: 'Safer Operations', text: 'Detect risks early and enable proactive interventions.' },
];

const questions = [
  'Which assets are at risk of failure?',
  'Do we have the right spares in the right locations?',
  'How can we reduce maintenance costs without increasing risk?',
  'What is the optimal maintenance strategy for critical assets?',
  'How can we avoid spare part stockouts and excess inventory?',
];

/* img = full path from public/ folder (extension ke saath). Har card ke liye alag file daalo.
   href = industry page ka link (header ke Industries menu se). */
const industries: { name: string; text: string; img: string; href?: string }[] = [
  {
    name: 'Manufacturing',
    text: 'Ensure continuous production with high asset uptime.',
    img: '/assets/images/manufacturing2.jpg',
    href: '/industries/manufacturing',
  },
  {
    name: 'Energy & Utilities',
    text: 'Manage critical assets and spares for uninterrupted operations.',
    img: '/assets/images/Utilities-&-Energy.jpg',
    href: '/industries/energy-utilities',
  },
  {
    name: 'Aviation & Airports',
    text: 'Optimise MRO and spare inventory for aircraft and ground assets.',
    img: '/assets/images/AIRCRAFT%20(1).jpg',
    href: '/industries/airlines-airports',
  },
  {
    name: 'Auto & Industrial',
    text: 'Reduce breakdowns and improve maintenance planning.',
    img: '/assets/images/Auto-&-Industrial1.jpg',
  },
  {
    name: 'Facilities Management',
    text: 'Manage building assets and spares efficiently.',
    img: '/assets/images/cis.jpg',
  },
  {
    name: 'Pharma & Healthcare',
    text: 'Maintain equipment uptime and ensure compliance.',
    img: '/assets/images/enrgy4.png',
    href: '/industries/pharma-lifescience',
  },
];

const useCases = [
  { title: 'Predictive Asset Failure', text: 'Predict failures using sensor, maintenance and historical data.' },
  { title: 'Spare Parts Demand Forecasting', text: 'Forecast spare part demand using ML models.' },
  { title: 'Maintenance Planning Optimisation', text: 'Recommend optimal maintenance schedules.' },
  { title: 'Spare Parts Inventory Optimisation', text: 'Determine right stock levels and allocation across locations.' },
  { title: 'Asset Health Monitoring', text: 'Monitor real-time asset health using IoT and AI.' },
  { title: 'Work Order Intelligence', text: 'Prioritise and schedule work orders based on risk and criticality.' },
];

const problems = [
  'Unplanned equipment downtime',
  'Excess and obsolete spare inventory',
  'Inaccurate spare part demand forecasting',
  'High maintenance costs',
  'Lack of visibility into asset health and performance',
];

const RESOURCES_HREF = '/insights/playbooks-downloads';

const resources = [
  { name: 'Asset Health Assessment Template', type: 'Excel' },
  { name: 'Spare Parts Optimisation Framework', type: 'PPT' },
  { name: 'Maintenance Strategy Checklist', type: 'PDF' },
  { name: 'Asset Performance Dashboard Template', type: 'Excel' },
  { name: 'Predictive Maintenance Playbook', type: 'PPT' },
];

/* Assumed content: the screenshot cuts off at "The Journey to Impact" */
const journey = [
  { title: 'Connect', text: 'Bring asset, maintenance and inventory data together.' },
  { title: 'Predict', text: 'Spot failure risk and demand shifts early.' },
  { title: 'Plan', text: 'Schedule maintenance and stock the right spares.' },
  { title: 'Improve', text: 'Track results and keep refining.' },
];

/* ===================== SCROLL REVEAL ===================== */
/* One fade-up on section content. Content stays visible if JS or IntersectionObserver is missing. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.querySelectorAll<HTMLElement>('.sec-head, .card, .uc-row, .res-row').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 5) * 70}ms`);
      io.observe(n);
    });
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function Assets() {
  const ref = useReveal();

  return (
    <div className="assets-page" ref={ref}>
      <main>
        {/* HERO */}
        <section className="as-hero">
          <div className="as-hero-bg"></div>
          <div className="as-hero-overlay"></div>
          <div className="op-container as-hero-inner">
            <div className="as-hero-content">

              <h1 className="as-hero-title">
                Asset &amp; Spare
                <br />
                <span>Intelligence</span>
              </h1>
              <p className="as-hero-lead">Maximise asset performance. Minimise downtime. Optimise spare parts.</p>
              <p className="as-hero-text">
                Use your data, business rules and AI to predict failures, optimise spare inventory, plan
                maintenance and improve asset life, across plants, fleets and facilities.
              </p>
              <div className="as-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Book a Discovery Session</Link>
                <Link className="btn-outline" href="#use-cases">Explore Use Cases</Link>
              </div>
            </div>

          </div>
        </section>

        {/* TRANSFORM */}
        <section className="op-section op-section--white">
          <div className="op-container">
            <div className="transform-grid">
              <div className="sec-head">
                <div className="sec-badge">Asset &amp; Spare Intelligence</div>
                <h2 className="sec-title">Transform asset management into a strategic advantage</h2>
                <p className="sec-sub sec-sub--base">
                  Move from reactive to predictive and intelligent asset management. Reduce unplanned
                  downtime, optimise spare parts inventory and improve asset utilisation across your operations.
                </p>
              </div>
              <div className="benefit-grid">
                {benefits.map((b, i) => (
                  <div className={`card benefit${i === 0 ? ' benefit--dark' : ''}`} key={b.title}>
                    <div className="benefit-top">
                      <span className="benefit-num">{String(i + 1).padStart(2, '0')}</span>
                      <Icon name={b.icon} />
                    </div>
                    <h3 className="card-title">{b.title}</h3>
                    <p className="card-text">{b.text}</p>
                  </div>
                ))}
                <div className="benefit-photo" role="img" aria-label="Engineer inspecting industrial equipment"></div>
              </div>
            </div>
          </div>
        </section>

        {/* KEY QUESTIONS */}
        <section className="op-section op-section--slate">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Real Decisions. Real Impact.</div>
              <h2 className="sec-title">Key Business Questions</h2>
              <p className="sec-sub sec-sub--base">Turn your asset and spare parts questions into intelligent decisions.</p>
            </div>
            <div className="q-grid">
              {questions.map((q) => (
                <div className="card q-card" key={q}>
                  <span className="q-mark"><Icon name="q" /></span>
                  <p className="q-text">{q}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="op-section op-section--white">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Industry Applications</div>
              <h2 className="sec-title">Industry Scenarios</h2>
              <p className="sec-sub sec-sub--base">Real-world scenarios to solve common asset and spare challenges.</p>
            </div>
            <div className="ind-grid">
              {industries.map((ind) => {
                const body = (
                  <>
                    <div className="ind-img" style={{ background: '#f4f8fd' }}>
                      <img src={ind.img} alt={ind.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', maxWidth: 'none' }} />
                    </div>
                    <div className="ind-body">
                      <h3 className="card-title">{ind.name}</h3>
                      <p className="card-text">{ind.text}</p>
                    </div>
                  </>
                );
                /* href na ho to card plain article rahega (click nahi hoga) */
                return ind.href ? (
                  <Link className="card ind-card" key={ind.name} href={ind.href}>
                    {body}
                  </Link>
                ) : (
                  <article className="card ind-card" key={ind.name}>
                    {body}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* AI USE CASES */}
        <section className="op-section op-section--slate" id="use-cases">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">From Insight to Action</div>
              <h2 className="sec-title">AI-Enabled Use Cases</h2>
              <p className="sec-sub sec-sub--base">Turn asset and spare data into actionable intelligence with AI.</p>
            </div>
            <div className="uc-grid">
              <div className="uc-list">
                {useCases.map((u, i) => (
                  <div className="uc-row" key={u.title}>
                    <span className="uc-num">{String(i + 1).padStart(2, '0')}</span>
                    <div className="uc-body">
                      <h3 className="uc-title">{u.title}</h3>
                      <p className="card-text">{u.text}</p>
                    </div>
                    <Link className="uc-link" href="/tellusyourproblem#tell-details">Use Case</Link>
                  </div>
                ))}
              </div>
              <div className="uc-photo" role="img" aria-label="Asset health dashboard with maintenance recommendations"></div>
            </div>
          </div>
        </section>

        {/* PROBLEMS + RESOURCES */}
        <section className="op-section op-section--white">
          <div className="op-container">
            <div className="split-grid">
              <div>
                <div className="sec-head">
                  <h2 className="sec-title">52 Asset &amp; Spare Problems We Solve</h2>
                  <p className="sec-sub sec-sub--base">Real problems. Practical solutions.</p>
                </div>
                <div className="panel">
                  {problems.map((p) => (
                    <details className="acc" key={p}>
                      <summary>
                        <Icon name="plus" />
                        {p}
                      </summary>
                      <p className="card-text">Explore how Orane approaches this problem with your data and business rules.</p>
                    </details>
                  ))}
                </div>
              </div>
              <div>
                <div className="sec-head">
                  <h2 className="sec-title">Resources &amp; Downloads</h2>
                  <p className="sec-sub sec-sub--base">Templates, frameworks and case examples.</p>
                </div>
                <div className="panel">
                  {resources.map((r) => (
                    <Link className="res-row" href={RESOURCES_HREF} key={r.name}>
                      <span className={`res-icon res-icon--${r.type.toLowerCase()}`}><Icon name="doc" /></span>
                      <span className="res-body">
                        <strong>{r.name}</strong>
                        <small>{r.type}</small>
                      </span>
                      <span className="res-dl" aria-label={`Download ${r.name}`}><Icon name="down" /></span>
                    </Link>
                  ))}
                </div>
                <div className="panel-foot">
                  <Link className="btn-primary" href={RESOURCES_HREF}>View All Asset &amp; Spare Resources</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* JOURNEY */}
        <section className="op-section op-section--slate">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">The Journey to Impact</div>
              <h2 className="sec-title">From Data to Dependable Assets</h2>
            </div>
            <ol className="journey">
              {journey.map((j, i) => (
                <li className="card journey-step" key={j.title}>
                  <span className="benefit-num">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="card-title">{j.title}</h3>
                  <p className="card-text">{j.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="as-cta" id="contact">
          <div className="as-cta-bg"></div>
          <div className="as-cta-overlay"></div>
          <div className="op-container as-cta-inner">
            <h2 className="as-cta-title">Stop Reacting to Breakdowns.</h2>
            <p className="as-cta-text">Predict earlier. Stock smarter. Keep assets running.</p>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Tell Us Your Asset Problem</Link>
          </div>
        </section>
      </main>
    </div>
  );
}